import http from "@services/httpService"

/**
 * Định dạng file xuất — khớp `PicoSolutionConsts.FileTypeEnum` của backend.
 * Chỉ Excel và Pdf được hỗ trợ; Word có trong enum backend nhưng tab 360 không dùng.
 */
export const ENTITY360_FILE_TYPE = {
  pdf: 1,
  excel: 3,
} as const

export type Entity360FileType =
  (typeof ENTITY360_FILE_TYPE)[keyof typeof ENTITY360_FILE_TYPE]

/** Kết quả một lượt xuất — token để tải qua `api/File/DownloadTempFile`. */
export interface IEntity360ExportResult {
  fileName: string
  fileType: string
  fileToken: string
}

/**
 * Tab 360 (Feature-0193). Backend: `Entity360AppService`
 * (`api/services/app/Entity360/*`). Mọi method là read-only.
 */

/** Loại thực thể — khớp enum `Module` của backend. Customer 360 dùng EXISTINGCUSTOMER. */
export const ENTITY360_MODULE = {
  existingCustomer: 148,
  contract: 130,
  project: 149,
  agency: 124,
  /** Unit 360 (SKLCRMF01-124) — trục căn hộ, gom lịch sử sang tay của mọi hợp đồng trên căn. */
  unit: 123,
} as const

/** Khoá cụm — khớp `Entity360ClusterKeys` của backend. */
export const ENTITY360_CLUSTER_KEYS = [
  "contracts",
  "contractTransfers",
  "bookings",
  "payments",
  "debtAging",
  "feeNotifications",
  "handoverPinkBook",
  "feedback",
  "changeRequests",
  "documents",
  "activities",
  "tasks",
] as const

export type Entity360ClusterKey = (typeof ENTITY360_CLUSTER_KEYS)[number]

/**
 * Trần một trang cụm ở backend — mirror của `Entity360Consts.MaxClusterPageSize`.
 *
 * Gửi đúng con số này khi người dùng mở bảng đầy đủ. Backend vẫn tự kẹp qua `Entity360PagingGuard` nên
 * gửi lớn hơn cũng vô ích; mirror ở đây chỉ để FE khỏi đoán một con số.
 */
export const ENTITY360_MAX_CLUSTER_PAGE_SIZE = 100

export interface IEntity360Kpi {
  /** Backend trả KHOÁ i18n, không trả chuỗi đã dịch. */
  labelKey: string
  value?: number | null
  clusterKey?: string
  /** `number` | `currency` | `date` — FE quyết định cách định dạng. */
  valueKind: string
  dateValue?: string | null
}

export interface IEntity360ClusterMeta {
  key: string
  totalCount: number
}

/**
 * Một dòng chi tiết của bản ghi, hiện khi người dùng hover vào dòng cụm.
 *
 * Backend trả KHOÁ i18n cho nhãn (`labelKey`) chứ không trả chuỗi đã dịch, giống mọi nhãn khác của tab.
 */
export interface IEntity360ClusterDetailLine {
  labelKey: string
  value?: string | null
  /** Chuỗi ISO — cùng kiểu với `date` của dòng cụm; FE định dạng bằng đúng helper của cột ngày. */
  date?: string | null
}

export interface IEntity360ClusterItem {
  id: number
  code?: string | null
  title?: string | null
  subTitle?: string | null
  date?: string | null
  amount?: number | null
  statusLabel?: string | null
  deepLink?: string | null
  /** true = lấy được nhờ bắc cầu CCCD, không phải nhờ khoá ngoại. */
  isCcccdMatched: boolean
  /**
   * Bên TRƯỚC của một dòng mô tả sự chuyển giao (ví dụ chuyển nhượng hợp đồng).
   *
   * Ba trường dưới đây cố ý TỔNG QUÁT, không gắn với cụm nào: thẻ cụm dùng chung nhìn dữ liệu để quyết
   * cách render (có `fromLabel`/`toLabel` thì hiện "trước → sau", có `detailLines` thì bọc hover), nên
   * cụm mới nào cũng dùng lại được mà không phải rẽ nhánh theo khoá cụm ở FE.
   */
  fromLabel?: string | null
  /** Bên SAU của sự chuyển giao. */
  toLabel?: string | null
  /** Thông tin đầy đủ của bản ghi, hiện khi hover — bảng chỉ đủ chỗ cho phần tóm tắt. */
  detailLines?: IEntity360ClusterDetailLine[] | null
}

export interface IEntity360Cluster {
  key: string
  totalCount: number
  items: IEntity360ClusterItem[]
  /** Backend đã cắt bớt vì trần quét ⇒ thứ tự và tổng số KHÔNG phải toàn cảnh; FE phải cảnh báo. */
  isTruncated?: boolean
}

export interface IEntity360Overview {
  module: number
  entityId: number
  kpis: IEntity360Kpi[]
  /**
   * Giá trị `null` = cụm đã thiết kế nhưng CHƯA có provider ⇒ FE ẩn thẻ (yêu cầu 6.2).
   * Cụm bị ẩn vì thiếu quyền thì KHÔNG có mặt trong map.
   */
  clusters: Record<string, IEntity360ClusterMeta | null>
}

export interface ITimelineItem {
  type: string
  date: string
  title?: string | null
  description?: string | null
  deepLink?: string | null
  isCcccdMatched: boolean
}

class Entity360Service {
  public async getOverview(
    module: number,
    entityId: number
  ): Promise<IEntity360Overview> {
    const res = await http.get("api/services/app/Entity360/GetOverview", {
      params: { module, entityId },
    })
    return res.data.result
  }

  /**
   * `maxResultCount` mặc định để trống ⇒ backend dùng cỡ trang mặc định (10) cho thẻ xem trước.
   * Bảng đầy đủ trong drawer phải truyền `MAX_CLUSTER_PAGE_SIZE`, nếu không nó hứa "xem tất cả" mà
   * chỉ nhận về 10 dòng.
   */
  public async getCluster(
    module: number,
    entityId: number,
    clusterKey: string,
    maxResultCount?: number
  ): Promise<IEntity360Cluster | null> {
    const res = await http.get("api/services/app/Entity360/GetCluster", {
      params: { module, entityId, clusterKey, maxResultCount },
    })
    return res.data.result
  }

  /**
   * Dựng file xuất ở backend và nhận token tải về.
   *
   * `Export*` không phải tiền tố Get/Create/Update/Delete nên ABP map sang POST — không thêm
   * `[HttpPost]` ở backend, đó là quy ước của repo.
   *
   * Chỉ trả token; việc tải do `fileService.downloadTempFile` lo, giống mọi bản xuất khác của hệ thống.
   */
  public async export(
    module: number,
    entityId: number,
    fileType: Entity360FileType
  ): Promise<IEntity360ExportResult> {
    const res = await http.post("api/services/app/Entity360Export/Export", {
      module,
      entityId,
      fileType,
    })
    return res.data.result
  }

  public async getTimeline(
    module: number,
    entityId: number,
    filter?: Record<string, unknown>
  ): Promise<{ totalCount: number; items: ITimelineItem[] }> {
    const res = await http.get("api/services/app/Entity360/GetTimeline", {
      params: { module, entityId, ...(filter ?? {}) },
    })
    return res.data.result
  }
}

export default new Entity360Service()
